import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import {API_URL} from '../../constants';
import {AuthContext} from '../AuthContext';
import {AXIOS_NOT_FOUND_REDIRECT_OPT_OUT} from '../../axiosNotFoundRedirect';

export const CUSTOMER_DOCUMENT_LIMIT = 10;
export const CUSTOMER_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const DOCUMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

export const getCustomerDocumentError = (error, fallback) => {
    const errors = error?.response?.data?.errors;

    if (errors && typeof errors === 'object') {
        const message = Object.values(errors)
            .flatMap((value) => Array.isArray(value) ? value : [value])
            .filter(Boolean)
            .join(' ');

        if (message) return message;
    }

    return error?.response?.data?.message || fallback;
};

const readBlobText = (blob) => {
    if (typeof blob.text === 'function') return blob.text();

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
};

export const getCustomerDocumentDownloadError = async (error, fallback) => {
    const responseData = error?.response?.data;

    if (typeof Blob === 'undefined' || !(responseData instanceof Blob)) {
        return getCustomerDocumentError(error, fallback);
    }

    try {
        const text = await readBlobText(responseData);
        const decodedData = JSON.parse(text);

        return getCustomerDocumentError({
            response: {
                ...error.response,
                data: decodedData,
            },
        }, fallback);
    } catch (decodeError) {
        return fallback;
    }
};

export const validateCustomerDocument = (file) => {
    if (!file) return 'Select a document to upload.';

    const extension = file.name?.split('.').pop()?.toLowerCase();
    const hasAllowedExtension = ALLOWED_EXTENSIONS.includes(extension);
    const hasAllowedMimeType = !file.type || ALLOWED_MIME_TYPES.includes(file.type);

    if (!hasAllowedExtension || !hasAllowedMimeType) {
        return 'Documents must be PDF, JPEG, or PNG files.';
    }

    if (file.size > CUSTOMER_DOCUMENT_MAX_BYTES) {
        return 'Documents must be 10 MB or smaller.';
    }

    return null;
};

export const formatDocumentSize = (bytes) => {
    const size = Number(bytes);
    if (!Number.isFinite(size) || size < 0) return 'Unknown size';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDocumentType = (mimeType) => ({
    'application/pdf': 'PDF',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
}[mimeType] || 'Document');

const formatDocumentDate = (date) => {
    if (!date) return 'Unknown date';

    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return 'Unknown date';

    return parsed.toLocaleString();
};

const safeDownloadName = (name, id) => {
    const basename = String(name || '').split(/[\\/]/).pop();
    const sanitized = Array.from(basename)
        .filter((character) => {
            const code = character.charCodeAt(0);
            return code >= 32 && code !== 127;
        })
        .join('')
        .trim();

    return sanitized || `customer-document-${id}`;
};

const CustomerDocuments = ({userId, canManage = false}) => {
    const {token, setToken} = useContext(AuthContext);
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [listLoadFailed, setListLoadFailed] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [activeMutation, setActiveMutation] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [previewingId, setPreviewingId] = useState(null);
    const [preview, setPreview] = useState(null);
    const [showDocuments, setShowDocuments] = useState(false);
    const activeUserIdRef = useRef(String(userId));
    const listRequestIdRef = useRef(0);
    const mutationRequestIdRef = useRef(0);
    const downloadRequestIdRef = useRef(0);
    const previewRequestIdRef = useRef(0);
    const previewUrlRef = useRef(null);
    const uploadInputRef = useRef(null);
    activeUserIdRef.current = String(userId);

    const isCurrentRequest = useCallback((requestedUserId, requestId, requestRef) => (
        activeUserIdRef.current === requestedUserId && requestRef.current === requestId
    ), []);

    const handleUnauthorized = useCallback((error) => {
        if (error?.response?.status !== 401) return false;

        localStorage.removeItem('authToken');
        setToken(null);
        navigate('/');
        return true;
    }, [navigate, setToken]);

    const clearPreview = useCallback(() => {
        previewRequestIdRef.current += 1;
        setPreviewingId(null);
        setPreview(null);

        if (previewUrlRef.current) {
            window.URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
    }, []);

    const loadDocuments = useCallback(async () => {
        const requestedUserId = String(userId);
        const requestId = ++listRequestIdRef.current;
        setIsLoading(true);
        setListLoadFailed(false);
        setErrorMessage('');

        try {
            const response = await axios.get(
                `${API_URL}/api/admin/users/${userId}/documents`,
                {headers: {Authorization: `Bearer ${token}`}},
            );

            if (!isCurrentRequest(requestedUserId, requestId, listRequestIdRef)) return;

            setDocuments(Array.isArray(response.data?.data) ? response.data.data : []);
            setListLoadFailed(false);
        } catch (error) {
            if (!isCurrentRequest(requestedUserId, requestId, listRequestIdRef)) return;
            if (handleUnauthorized(error)) return;

            setDocuments([]);
            setListLoadFailed(true);
            setErrorMessage(getCustomerDocumentError(
                error,
                'Unable to load customer documents. Please try again later.',
            ));
        } finally {
            if (isCurrentRequest(requestedUserId, requestId, listRequestIdRef)) {
                setIsLoading(false);
            }
        }
    }, [handleUnauthorized, isCurrentRequest, token, userId]);

    useEffect(() => {
        listRequestIdRef.current += 1;
        mutationRequestIdRef.current += 1;
        downloadRequestIdRef.current += 1;
        clearPreview();
        setDocuments([]);
        setActiveMutation(null);
        setDownloadingId(null);
        setListLoadFailed(false);
        setErrorMessage('');
        loadDocuments();

        return () => {
            listRequestIdRef.current += 1;
            mutationRequestIdRef.current += 1;
            downloadRequestIdRef.current += 1;
            previewRequestIdRef.current += 1;

            if (previewUrlRef.current) {
                window.URL.revokeObjectURL(previewUrlRef.current);
                previewUrlRef.current = null;
            }
        };
    }, [clearPreview, loadDocuments]);

    const uploadDocument = async (file) => {
        if (
            !canManage
            || activeMutation
            || isLoading
            || listLoadFailed
            || documents.length >= CUSTOMER_DOCUMENT_LIMIT
        ) return;

        const validationError = validateCustomerDocument(file);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        const requestedUserId = String(userId);
        const requestId = ++mutationRequestIdRef.current;
        const formData = new FormData();
        formData.append('document', file);
        setErrorMessage('');
        setActiveMutation({type: 'upload'});

        try {
            await axios.post(`${API_URL}/api/admin/users/${userId}/documents`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!isCurrentRequest(requestedUserId, requestId, mutationRequestIdRef)) return;
            await loadDocuments();
        } catch (error) {
            if (!isCurrentRequest(requestedUserId, requestId, mutationRequestIdRef)) return;
            if (handleUnauthorized(error)) return;

            setErrorMessage(getCustomerDocumentError(
                error,
                'Unable to upload the document. Please try again later.',
            ));
        } finally {
            if (isCurrentRequest(requestedUserId, requestId, mutationRequestIdRef)) {
                setActiveMutation(null);
            }
        }
    };

    const replaceDocument = async (customerDocument, file) => {
        if (!canManage || activeMutation) return;

        const validationError = validateCustomerDocument(file);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        if (!window.confirm(`Replace "${customerDocument.original_name}"?`)) return;

        const requestedUserId = String(userId);
        const requestId = ++mutationRequestIdRef.current;
        const formData = new FormData();
        formData.append('document', file);
        setErrorMessage('');
        setActiveMutation({type: 'replace', id: customerDocument.id});

        try {
            await axios.post(
                `${API_URL}/api/admin/users/${userId}/documents/${customerDocument.id}/replace`,
                formData,
                {
                    ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                },
            );

            if (!isCurrentRequest(requestedUserId, requestId, mutationRequestIdRef)) return;
            await loadDocuments();
        } catch (error) {
            if (!isCurrentRequest(requestedUserId, requestId, mutationRequestIdRef)) return;
            if (handleUnauthorized(error)) return;

            setErrorMessage(getCustomerDocumentError(
                error,
                'Unable to replace the document. Please try again later.',
            ));
        } finally {
            if (isCurrentRequest(requestedUserId, requestId, mutationRequestIdRef)) {
                setActiveMutation(null);
            }
        }
    };

    const deleteDocument = async (customerDocument) => {
        if (!canManage || activeMutation) return;
        if (!window.confirm(`Delete "${customerDocument.original_name}"? This cannot be undone.`)) return;

        const requestedUserId = String(userId);
        const requestId = ++mutationRequestIdRef.current;
        setErrorMessage('');
        setActiveMutation({type: 'delete', id: customerDocument.id});

        try {
            await axios.delete(
                `${API_URL}/api/admin/users/${userId}/documents/${customerDocument.id}`,
                {
                    ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
                    headers: {Authorization: `Bearer ${token}`},
                },
            );

            if (!isCurrentRequest(requestedUserId, requestId, mutationRequestIdRef)) return;
            await loadDocuments();
        } catch (error) {
            if (!isCurrentRequest(requestedUserId, requestId, mutationRequestIdRef)) return;
            if (handleUnauthorized(error)) return;

            setErrorMessage(getCustomerDocumentError(
                error,
                'Unable to delete the document. Please try again later.',
            ));
        } finally {
            if (isCurrentRequest(requestedUserId, requestId, mutationRequestIdRef)) {
                setActiveMutation(null);
            }
        }
    };

    const downloadDocument = async (customerDocument) => {
        if (downloadingId !== null) return;

        const requestedUserId = String(userId);
        const requestId = ++downloadRequestIdRef.current;
        setErrorMessage('');
        setDownloadingId(customerDocument.id);

        try {
            const response = await axios.get(
                `${API_URL}/api/admin/users/${userId}/documents/${customerDocument.id}/download`,
                {
                    ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
                    headers: {Authorization: `Bearer ${token}`},
                    responseType: 'blob',
                },
            );

            if (!isCurrentRequest(requestedUserId, requestId, downloadRequestIdRef)) return;

            const url = window.URL.createObjectURL(response.data);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = safeDownloadName(customerDocument.original_name, customerDocument.id);
            link.rel = 'noopener';
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            if (!isCurrentRequest(requestedUserId, requestId, downloadRequestIdRef)) return;
            if (handleUnauthorized(error)) return;

            const message = await getCustomerDocumentDownloadError(
                error,
                'Unable to download the document. Please try again later.',
            );
            if (!isCurrentRequest(requestedUserId, requestId, downloadRequestIdRef)) return;

            setErrorMessage(message);
        } finally {
            if (isCurrentRequest(requestedUserId, requestId, downloadRequestIdRef)) {
                setDownloadingId(null);
            }
        }
    };

    const previewDocument = async (customerDocument) => {
        if (previewingId !== null) return;

        if (!ALLOWED_MIME_TYPES.includes(customerDocument.mime_type)) {
            setErrorMessage('Only PDF, JPEG, and PNG documents can be previewed.');
            return;
        }

        const requestedUserId = String(userId);
        const requestId = ++previewRequestIdRef.current;
        setErrorMessage('');
        setPreviewingId(customerDocument.id);

        try {
            const response = await axios.get(
                `${API_URL}/api/admin/users/${userId}/documents/${customerDocument.id}/download`,
                {
                    ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
                    headers: {Authorization: `Bearer ${token}`},
                    responseType: 'blob',
                },
            );

            if (!isCurrentRequest(requestedUserId, requestId, previewRequestIdRef)) return;

            const documentBlob = response.data instanceof Blob
                ? response.data
                : new Blob([response.data], {type: customerDocument.mime_type});
            const previewBlob = documentBlob.type === customerDocument.mime_type
                ? documentBlob
                : new Blob([documentBlob], {type: customerDocument.mime_type});
            const url = window.URL.createObjectURL(previewBlob);

            if (!isCurrentRequest(requestedUserId, requestId, previewRequestIdRef)) {
                window.URL.revokeObjectURL(url);
                return;
            }

            if (previewUrlRef.current) {
                window.URL.revokeObjectURL(previewUrlRef.current);
            }
            previewUrlRef.current = url;
            setPreview({
                id: customerDocument.id,
                mimeType: customerDocument.mime_type,
                name: safeDownloadName(customerDocument.original_name, customerDocument.id),
                userId: requestedUserId,
                url,
            });
        } catch (error) {
            if (!isCurrentRequest(requestedUserId, requestId, previewRequestIdRef)) return;
            if (handleUnauthorized(error)) return;

            const message = await getCustomerDocumentDownloadError(
                error,
                'Unable to preview the document. Please try again later.',
            );
            if (!isCurrentRequest(requestedUserId, requestId, previewRequestIdRef)) return;

            setErrorMessage(message);
        } finally {
            if (isCurrentRequest(requestedUserId, requestId, previewRequestIdRef)) {
                setPreviewingId(null);
            }
        }
    };

    const mutationInProgress = activeMutation !== null;
    const atDocumentLimit = documents.length >= CUSTOMER_DOCUMENT_LIMIT;
    const uploadUnavailable = mutationInProgress || isLoading || listLoadFailed || atDocumentLimit;
    const currentPreview = preview?.userId === String(userId) ? preview : null;
    const previewTitleId = `customer-document-preview-title-${userId}-${currentPreview?.id || 'closed'}`;

    return (
        <div className="card mb-3" data-testid="customer-documents">
            <div className="card-body">
                <h5 className="text-center mb-0">
                    <button
                        type="button"
                        className="btn btn-link p-0"
                        style={{fontSize: 'inherit', fontWeight: 'inherit', textDecoration: 'none'}}
                        aria-expanded={showDocuments}
                        aria-controls={`customer-documents-content-${userId}`}
                        onClick={() => setShowDocuments((isShown) => !isShown)}
                    >
                        Documents {showDocuments ? '▼' : '▶'}
                    </button>
                </h5>

                <div
                    id={`customer-documents-content-${userId}`}
                    data-testid="customer-documents-content"
                    className={`fade-section customer-documents-section ${showDocuments ? 'show' : 'hide'}`}
                    hidden={!showDocuments}
                >
                    <div className="d-flex justify-content-end mt-2 mb-2">
                        <span className="text-muted" aria-label="Document count">
                            {isLoading || listLoadFailed ? '—' : documents.length} / {CUSTOMER_DOCUMENT_LIMIT}
                        </span>
                    </div>

                    <p className="small text-muted">
                        PDF, JPEG, or PNG. Maximum 10 MB per file. Documents are private and accessed securely.
                    </p>

                    {errorMessage && (
                        <div className="alert alert-danger" role="alert">{errorMessage}</div>
                    )}

                    {canManage && (
                    <div className="mb-3">
                        <input
                            ref={uploadInputRef}
                            id={`customer-document-upload-${userId}`}
                            type="file"
                            className="d-none"
                            accept={DOCUMENT_ACCEPT}
                            aria-label="Upload customer document"
                            disabled={uploadUnavailable}
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                event.target.value = '';
                                uploadDocument(file);
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={uploadUnavailable}
                            onClick={() => uploadInputRef.current?.click()}
                        >
                            {activeMutation?.type === 'upload' ? 'Uploading...' : 'Upload Document'}
                        </button>
                        {atDocumentLimit && (
                            <span className="small text-muted ml-2">Delete a document before uploading another.</span>
                        )}
                    </div>
                    )}

                    {isLoading ? (
                    <div className="text-center py-3" role="status">
                        <span className="spinner-border spinner-border-sm" aria-hidden="true"/>
                        <span className="sr-only">Loading documents...</span>
                    </div>
                ) : listLoadFailed ? (
                    <div className="text-center py-2">
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadDocuments}>
                            Retry Documents
                        </button>
                    </div>
                ) : documents.length === 0 ? (
                    <div className="text-center text-muted py-2">No documents uploaded.</div>
                ) : (
                    <ul className="list-group">
                        {documents.map((customerDocument) => {
                            const isReplacing = activeMutation?.type === 'replace'
                                && activeMutation.id === customerDocument.id;
                            const isDeleting = activeMutation?.type === 'delete'
                                && activeMutation.id === customerDocument.id;

                            return (
                                <li className="list-group-item" key={customerDocument.id}>
                                    <div className="d-flex flex-wrap justify-content-between align-items-center">
                                        <div className="mr-3 mb-2 mb-md-0" style={{minWidth: 0}}>
                                            <div className="font-weight-bold text-break">
                                                {customerDocument.original_name}
                                            </div>
                                            <div className="small text-muted">
                                                {formatDocumentType(customerDocument.mime_type)}
                                                {' · '}
                                                {formatDocumentSize(customerDocument.size)}
                                                {' · '}
                                                Uploaded {formatDocumentDate(customerDocument.created_at)}
                                            </div>
                                        </div>
                                        <div className="btn-group" role="group" aria-label={`Actions for ${customerDocument.original_name}`}>
                                            <button
                                                type="button"
                                                className="btn btn-outline-info btn-sm"
                                                disabled={previewingId !== null}
                                                onClick={() => previewDocument(customerDocument)}
                                            >
                                                {previewingId === customerDocument.id ? 'Opening...' : 'Preview'}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                disabled={downloadingId !== null}
                                                onClick={() => downloadDocument(customerDocument)}
                                            >
                                                {downloadingId === customerDocument.id ? 'Downloading...' : 'Download'}
                                            </button>
                                            {canManage && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        disabled={mutationInProgress}
                                                        onClick={() => window.document
                                                            .getElementById(`replace-customer-document-${customerDocument.id}`)
                                                            ?.click()}
                                                    >
                                                        {isReplacing ? 'Replacing...' : 'Replace'}
                                                    </button>
                                                    <input
                                                        id={`replace-customer-document-${customerDocument.id}`}
                                                        type="file"
                                                        className="d-none"
                                                        accept={DOCUMENT_ACCEPT}
                                                        aria-label={`Replace ${customerDocument.original_name}`}
                                                        disabled={mutationInProgress}
                                                        onChange={(event) => {
                                                            const file = event.target.files?.[0];
                                                            event.target.value = '';
                                                            replaceDocument(customerDocument, file);
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        disabled={mutationInProgress}
                                                        onClick={() => deleteDocument(customerDocument)}
                                                    >
                                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    )}
                </div>
            </div>

            <Modal
                show={currentPreview !== null}
                onHide={clearPreview}
                size="xl"
                centered
                aria-labelledby={previewTitleId}
            >
                <Modal.Header>
                    <Modal.Title id={previewTitleId}>
                        {currentPreview?.name || 'Document preview'}
                    </Modal.Title>
                    <button
                        type="button"
                        className="close"
                        aria-label="Close"
                        onClick={clearPreview}
                    >
                        <i className="fa fa-times" aria-hidden="true"/>
                    </button>
                </Modal.Header>
                <Modal.Body className="p-2 text-center">
                    {currentPreview?.mimeType === 'application/pdf' ? (
                        <iframe
                            title={`Preview of ${currentPreview.name}`}
                            src={currentPreview.url}
                            style={{width: '100%', height: '70vh', border: 0}}
                        />
                    ) : currentPreview ? (
                        <img
                            src={currentPreview.url}
                            alt={`Preview of ${currentPreview.name}`}
                            style={{maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain'}}
                        />
                    ) : null}
                </Modal.Body>
            </Modal>

            <style>{`
                .fade-section.customer-documents-section {
                    max-height: 0;
                    opacity: 0;
                    overflow: hidden;
                    transition: max-height 0.4s ease-in-out, opacity 0.4s ease-in-out;
                }

                .fade-section.customer-documents-section.show {
                    max-height: 6000px;
                    opacity: 1;
                }

                .fade-section.customer-documents-section.hide {
                    max-height: 0;
                    opacity: 0;
                }
            `}</style>
        </div>
    );
};

export default CustomerDocuments;
